import React, { memo, useLayoutEffect, useRef, useState } from 'react';

import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

import { FieldErrors } from 'entities/modal/model/types/modalFieldErrors';
import { useClickOutside, useSetInputFocus } from 'shared/hooks';
import { TModalMode } from 'shared/model/types/modalMode.type';
import { Icon, ModalFieldErrorSpan, Option, Radio, UiButton } from 'shared/ui';

import cls from './ExecutorsStaff.module.css';
import { ExecutorItem } from './ui/ExecutorItem';
import { useExecutorsStaff } from './useExecutorsStaff';
import { Task } from '../../entities/task/model/types/tasks';

interface ExecutorsStaffProps {
    modalMode: TModalMode;
    text: string;
    error: FieldErrors['executor'];
    highestRole: Task['context']['highest_role'] | undefined;
    setFieldErrors: React.Dispatch<React.SetStateAction<FieldErrors>>;
    classes?: string;
    isDisabled?: boolean;
}

export const ExecutorsStaff = memo(
    ({
        isDisabled = false,
        modalMode,
        text,
        highestRole,
        setFieldErrors,
        classes,
        error,
    }: ExecutorsStaffProps) => {
        const {
            isOpen,
            optionFilter,
            filteredOptions,
            selectedStaff,
            handleOptionClick,
            handleInputChange,
            toggleDropdown,
            setIsOpen,
            employees,
        } = useExecutorsStaff(modalMode, isDisabled, setFieldErrors);
        const { t } = useTranslation();
        const [openedListStyle, setOpenedListStyle] = useState<React.CSSProperties>({});

        const { rootRef } = useClickOutside({ isOpen, setIsOpen });
        const triggerRef = useRef<HTMLDivElement | null>(null);
        const inputRef = useSetInputFocus({ isOpen });

        useLayoutEffect(() => {
            const calcRect = () => {
                if (!isOpen) {
                    setOpenedListStyle({});
                    return;
                }

                if (!triggerRef.current) return;

                const rect = triggerRef.current.getBoundingClientRect();
                const spaceBelow = window.innerHeight - rect.bottom;
                const openUpward = spaceBelow < 170;

                setOpenedListStyle({
                    position: 'fixed',
                    top: openUpward ? rect.top - 8 : rect.bottom + 4,
                    left: rect.left,
                    width: rect.width,
                    transform: openUpward ? 'translateY(-100%)' : 'none',
                });
            };

            calcRect();

            window.addEventListener('resize', calcRect);
            window.addEventListener('scroll', calcRect, true);

            return () => {
                window.removeEventListener('resize', calcRect);
                window.removeEventListener('scroll', calcRect, true);
            };
        }, [isOpen]);

        return (
            <div
                className={classNames(cls.SearchStaffButton, [classes], error.isError && cls.error)}
                ref={rootRef}
            >
                <div
                    ref={triggerRef}
                    className={classNames(cls.Container, isDisabled && cls.disabled)}
                    onClick={toggleDropdown}
                    title={t('Features.ExecutorsStaff.AttributeTitle.text')}
                >
                    <Icon iconName="taskExecutor" />
                    {!!selectedStaff.length &&
                        selectedStaff[0]?.name != t('Features.ExecutorsStaff.NotSelected.text') &&
                        selectedStaff.map((option) => (
                            <ExecutorItem
                                key={option.id}
                                toggleDropdown={toggleDropdown}
                                avatarUrl={option.avatar_url}
                                name={option.name}
                                isDisabled={isDisabled}
                            />
                        ))}
                    {!selectedStaff.length && (
                        <UiButton
                            className={cls.Button}
                            onClick={toggleDropdown}
                            btnText={
                                <span
                                    className={classNames(
                                        cls.ButtonText,
                                        isDisabled && cls.disabled
                                    )}
                                >
                                    {text}
                                </span>
                            }
                            startIcon={!isDisabled && <Icon iconName="plus" />}
                            variant="text"
                        />
                    )}
                    {selectedStaff[0]?.id === null && (
                        <UiButton
                            className={cls.Button}
                            onClick={toggleDropdown}
                            btnText={
                                <span
                                    className={classNames(
                                        cls.ButtonText,
                                        isDisabled && cls.disabled
                                    )}
                                >
                                    {text}
                                </span>
                            }
                            startIcon={!isDisabled && <Icon iconName="plus" />}
                            variant="text"
                        />
                    )}
                    {error.isError && <ModalFieldErrorSpan errorMessage={error.errorMessage} />}
                </div>
                {isOpen && (
                    <div className={cls.OpenedListContainer} style={openedListStyle}>
                        <div className={cls.filterInput}>
                            <input
                                ref={inputRef}
                                className={cls.input}
                                onChange={handleInputChange}
                                value={optionFilter}
                                onClick={(e) => e.stopPropagation()}
                            />
                        </div>
                        <ul className={cls.optionsUl}>
                            {optionFilter && optionFilter.length > 0 ? (
                                <>
                                    {filteredOptions.map((option) => {
                                        const isSelectedOption = !!selectedStaff.filter(
                                            (item) => item.id === option.id
                                        ).length;
                                        return (
                                            <Option
                                                key={option.id}
                                                option={option}
                                                onClick={handleOptionClick}
                                                isDisabled={!option || option.id === null}
                                                CheckedStatusAddon={
                                                    <Radio
                                                        isSelected={isSelectedOption}
                                                        classes={cls.radioAddedClasses}
                                                    />
                                                }
                                                styles={cls.option}
                                            />
                                        );
                                    })}
                                </>
                            ) : (
                                <>
                                    <Option
                                        key="1"
                                        option={{
                                            id: null,
                                            name: t('Features.ExecutorsStaff.NotSelected.text'),
                                            avatar_url: '',
                                        }}
                                        onClick={handleOptionClick}
                                        isDisabled={false}
                                        CheckedStatusAddon={
                                            <Radio
                                                isSelected={
                                                    selectedStaff[0]?.name ===
                                                    t('Features.ExecutorsStaff.NotSelected.text')
                                                }
                                                classes={cls.radioAddedClasses}
                                            />
                                        }
                                        styles={classNames(cls.option, cls.disabledOption)}
                                    />
                                    {employees.map((option) => {
                                        const isSelectedOption = !!selectedStaff.filter(
                                            (item) => item.id === option.id
                                        ).length;
                                        return (
                                            <Option
                                                key={option.id}
                                                option={option}
                                                onClick={handleOptionClick}
                                                isDisabled={!option || option.id === null}
                                                CheckedStatusAddon={
                                                    <Radio
                                                        isSelected={isSelectedOption}
                                                        classes={cls.radioAddedClasses}
                                                    />
                                                }
                                                styles={cls.option}
                                            />
                                        );
                                    })}
                                </>
                            )}
                        </ul>
                    </div>
                )}
            </div>
        );
    }
);
